import React from 'react';
import css from './ListingpageContent.module.css';
import { PrimaryButton } from '../../components';
import Icons from '../../components/Icons/Icons';
import { FormattedMessage } from 'react-intl';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';

const formatMoney = (intl, amount, currency = 'USD') => {
  if (amount == null) return null;

  if (typeof amount === 'object' && amount.amount != null) {
    const val = amount.amount / 100;
    return intl.formatNumber(val, { style: 'currency', currency: amount.currency || currency });
  }

  const num = Number(amount);
  if (Number.isNaN(num)) return null;

  return intl.formatNumber(num, { style: 'currency', currency });
};

const normalizeMenuExamples = (publicData, services, intl) => {
  const menuExamples =
    publicData?.menuExamples || publicData?.menus || publicData?.sampleMenus || publicData?.menu;
  const menuArray = Array.isArray(menuExamples) ? menuExamples : menuExamples ? [menuExamples] : [];

  if (menuArray.length) {
    return menuArray.map((menu, index) => {
      if (typeof menu === 'string') {
        return {
          title: intl.formatMessage(
            { id: 'ListingpageContent.menuFallbackTitle' },
            { index: index + 1 }
          ),
          description: null,
          courses: [
            {
              label: intl.formatMessage({ id: 'ListingpageContent.menuMainCourse' }),
              value: menu,
            },
          ],
        };
      }

      const configuredCourses = Array.isArray(menu.courses)
        ? menu.courses
            .map(course => ({
              label: course.label || course.courseType || course.type,
              value: course.description || course.name || course.value,
            }))
            .filter(course => course.label && course.value)
        : null;
      const courses =
        configuredCourses?.length > 0
          ? configuredCourses
          : [
              {
                label: intl.formatMessage({ id: 'ListingpageContent.menuStarter' }),
                value: menu.starter,
              },
              {
                label: intl.formatMessage({ id: 'ListingpageContent.menuMainCourse' }),
                value: menu.mainCourse || menu.main,
              },
              {
                label: intl.formatMessage({ id: 'ListingpageContent.menuDessert' }),
                value: menu.dessert,
              },
            ].filter(course => course.value);

      return {
        title:
          menu.title ||
          menu.name ||
          intl.formatMessage({ id: 'ListingpageContent.menuFallbackTitle' }, { index: index + 1 }),
        description: menu.description,
        courses,
      };
    });
  }

  return services.slice(0, 3).map((service, index) => ({
    title:
      service?.name ||
      intl.formatMessage({ id: 'ListingpageContent.menuFallbackTitle' }, { index: index + 1 }),
    description: null,
    courses: [
      {
        label: intl.formatMessage({ id: 'ListingpageContent.menuMainCourse' }),
        value:
          service?.serviceDescription ||
          intl.formatMessage({ id: 'ListingpageContent.serviceFallbackDescription' }),
      },
    ],
  }));
};

function ListingpageContent(props) {
  const {
    intl,
    title,
    description,
    publicData,
    showAsIngress,
    bookingMode,
    reviews,
    fetchReviewsError,
    setSelectedService,
  } = props;

  const services = Array.isArray(publicData?.services) ? publicData.services : [];
  const gallery = Array.isArray(publicData?.gallery) ? publicData.gallery : [];
  const menuExamples = normalizeMenuExamples(publicData, services, intl);

  return (
    <div className={css.aboutSec}>
      <div className={css.aboutContentSec}>
        <h3>
          <FormattedMessage id="ListingpageContent.aboutTitle" values={{ title }} />
        </h3>

        <SectionTextMaybe text={description} showAsIngress />
      </div>

      <div className={css.aboutContentSec}>
        <h3>
          <FormattedMessage id="ListingpageContent.menuExamplesTitle" />
        </h3>

        {menuExamples.length ? (
          <div className={css.menuExamples}>
            {menuExamples.map((menu, index) => (
              <div className={css.menuExampleCard} key={`${menu.title}-${index}`}>
                <h4>{menu.title}</h4>
                {menu.description ? <p>{menu.description}</p> : null}
                <div className={css.menuCourses}>
                  {menu.courses.map(course => (
                    <div className={css.menuCourse} key={`${menu.title}-${course.label}`}>
                      <span>{course.label}</span>
                      <p>{course.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={css.emptyState}>
            <FormattedMessage id="ListingpageContent.noMenuExamples" />
          </p>
        )}
      </div>

      <div className={css.aboutContentSec}>
        <h3>
          <FormattedMessage id="ListingpageContent.servicesTitle" />
        </h3>

        <div className={css.servicesContent}>
          {services.length ? (
            services.map((s, index) => {
              const serviceName =
                s?.name ||
                intl.formatMessage(
                  { id: 'ListingpageContent.serviceFallbackName' },
                  { index: index + 1 }
                );
              const serviceDescription =
                s?.serviceDescription ||
                intl.formatMessage({ id: 'ListingpageContent.serviceFallbackDescription' });

              // Guests
              const minGuests = s?.minGuests ?? publicData?.minGuests;
              const maxGuests = s?.maxGuests ?? publicData?.maxGuests;

              // Price (your data shows "100", "200" strings)
              const priceText = formatMoney(intl, s?.price, publicData?.currency || 'USD');

              return (
                <div className={css.servicesBlock} key={s?.id || `${serviceName}-${index}`}>
                  <div className={css.serviceHead}>
                    <h2>{serviceName}</h2>

                    {priceText ? (
                      <div className={css.servicePrice}>
                        {priceText}{' '}
                        <span>
                          <FormattedMessage id="ListingpageContent.perGuest" />
                        </span>
                      </div>
                    ) : null}
                  </div>

                  <div className={css.serviceBody}>
                    <p>{serviceDescription}</p>

                    <div className={css.serviceCapacity}>
                      <span>
                        <Icons name="users" />{' '}
                        <FormattedMessage
                          id="ListingpageContent.guestsRange"
                          values={{ min: minGuests || 0, max: maxGuests || 0 }}
                        />
                      </span>

                      {/* If you have duration data in publicData.duration later, map it here */}
                      <span>
                        <Icons name="clock" />{' '}
                        <FormattedMessage id="ListingpageContent.durationFallback" />
                      </span>
                    </div>

                    <div className={css.sericeAction}>
                      {/* <SecondaryButton>
                        <FormattedMessage id="ListingpageContent.viewDetails" />
                      </SecondaryButton> */}
                      <PrimaryButton
                        disabled={bookingMode === 'hourly'}
                        onClick={() => setSelectedService?.({ id: s?.id })}
                      >
                        <FormattedMessage id="ListingpageContent.selectService" />
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <p>
              <FormattedMessage id="ListingpageContent.noServices" />
            </p>
          )}
        </div>
      </div>

      <div className={css.aboutContentSec}>
        <h3>
          <FormattedMessage id="ListingpageContent.galleryTitle" />
        </h3>

        <div className={css.gallarySec}>
          {gallery.length ? (
            <>
              {gallery.slice(0, 3).map((src, i) => (
                <div className={css.imgBlock} key={`${src}-${i}`}>
                  <img
                    src={src}
                    alt={intl.formatMessage(
                      { id: 'ListingpageContent.galleryImageAlt' },
                      { index: i + 1 }
                    )}
                  />
                </div>
              ))}
              <div className={css.imgBlock}>
                {gallery.length > 3 ? (
                  <span>
                    <FormattedMessage
                      id="ListingpageContent.galleryMore"
                      values={{ count: gallery.length - 3 }}
                    />
                  </span>
                ) : (
                  <span>
                    <FormattedMessage id="ListingpageContent.galleryMoreEmpty" />
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className={css.galleryEmpty}>
              <span>
                <FormattedMessage id="ListingpageContent.noGalleryImages" />
              </span>
            </div>
          )}
        </div>
      </div>
      <div className={css.aboutContentSec}>
        <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
      </div>
    </div>
  );
}

export default ListingpageContent;
